import {
  Autocomplete,
  Button,
  Flex,
  Grid,
  Label,
  SelectField,
  TextField,
} from "@aws-amplify/ui-react";
import { useEffect, useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { API, graphqlOperation } from "aws-amplify";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import { listParameters } from "graphql/queries";

const EquipmentTypeParameterForm = ({
  onSubmit,
  type = "create",
  initialValues,
  companies,
}) => {
  const [equipmentTypeParameterForm, setEquipmentTypeParameterForm] =
    useState(initialValues);
  const [parameters, setParameters] = useState({
    list: [],
    loading: false,
  });
  const [errors, setErrors] = useState({
    parameterID: "",
    parameterValue: "",
  });
  const validateErrors = useErrors({
    parameterID: "isRequired",
    parameterValue: "isRequired",
  });
  const parameterSelected = useMemo(
    () =>
      parameters.list.find(
        (parameterRec) =>
          parameterRec.id === equipmentTypeParameterForm.parameterID
      ),
    [parameters.list, equipmentTypeParameterForm.parameterID]
  );

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(equipmentTypeParameterForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (checkIncorrectValue("parameterID", parameters.list)) return;

    if (!hasErrors) {
      const { name: parameterName } = parameterSelected;
      onSubmit({ ...equipmentTypeParameterForm, parameterName });
    }
  };

  const checkIncorrectValue = (name, list) => {
    if (!list.some((rec) => rec.id === equipmentTypeParameterForm[name])) {
      setErrors((prevSt) => ({
        ...prevSt,
        [name]: strings.validations.incorrectValue,
      }));
      return true;
    }
    return false;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setEquipmentTypeParameterForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;

    fetchParameters({ name: value });
    setEquipmentTypeParameterForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    if (name === "parameterID")
      setEquipmentTypeParameterForm((prevSt) => ({
        ...prevSt,
        parameterValue: "",
      }));
    checkErrors(name, value);
  };

  const onSelect = (name, value) => {
    setEquipmentTypeParameterForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    if (name === "parameterID")
      setEquipmentTypeParameterForm((prevSt) => ({
        ...prevSt,
        parameterValue: "",
      }));
    checkErrors(name, value);
  };

  const onClearSelect = (name) => {
    const value = "";
    fetchParameters({ name: value });
    setEquipmentTypeParameterForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    if (name === "parameterID")
      setEquipmentTypeParameterForm((prevSt) => ({
        ...prevSt,
        parameterValue: "",
      }));
  };

  const resetFormValues = () => {
    setErrors({
      parameterID: "",
      parameterValue: "",
    });
    setEquipmentTypeParameterForm(initialValues);
    fetchParameters({ id: initialValues.parameterID });
  };

  async function fetchParameters({ name = "" }) {
    try {
      setParameters({ list: [], loading: true });
      const parametersData = await API.graphql(
        graphqlOperation(listParameters, {
          filter: {
            and: [
              {
                name: { contains: name },
              },
            ],
            or: companies.map((c) => ({ companies: { contains: c } })),
            deletedAt: { attributeExists: false },
          },
        })
      );
      const parametersItems = parametersData.data.listParameters.items;
      setParameters((prev) => ({ ...prev, list: parametersItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchParameters);
    } finally {
      setParameters((prev) => ({ ...prev, loading: false }));
    }
  }

  useEffect(() => {
    setEquipmentTypeParameterForm(initialValues);
    fetchParameters({ id: initialValues.parameterID });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    if (
      parameterSelected &&
      ["", null].some((i) => i === equipmentTypeParameterForm.parameterValue)
    ) {
      setEquipmentTypeParameterForm((prev) => ({
        ...prev,
        parameterValue: parameterSelected.defaultValue,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterSelected]);

  return (
    <>
      <Grid rowGap="15px" columnGap="15px" padding="20px">
        <Grid gap="15px" templateColumns={{ base: "1fr", large: "2fr 2fr" }}>
          <Flex direction="column" gap="8px">
            <Label htmlFor="parameterID">
              <LabelRequired text={strings.fields.name.label} />
            </Label>
            <Autocomplete
              id="parameterID"
              name="parameterID"
              isRequired
              placeholder={strings.fields.name.placeholder}
              value={
                parameterSelected?.name ??
                equipmentTypeParameterForm.parameterID
              }
              options={parameters.list.map((parameterRec) => ({
                id: parameterRec.id,
                label: parameterRec.name,
              }))}
              isLoading={parameters.loading}
              onClear={() => onClearSelect("parameterID")}
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("parameterID", id)}
              errorMessage={errors.parameterID}
              hasError={!!errors.parameterID}
              menuSlots={{ Empty: globalStrings.noResults }}
            />
          </Flex>
          {parameterSelected?.type === "list" ? (
            <SelectField
              name="parameterValue"
              value={equipmentTypeParameterForm.parameterValue}
              label={<LabelRequired text={strings.fields.value.label} />}
              onChange={onChange}
              errorMessage={errors.parameterValue}
              hasError={!!errors.parameterValue}
            >
              <option value="">{globalStrings.selectOption}</option>
              {JSON.parse(parameterSelected.values)?.map((paramVal) => (
                <option value={paramVal.value}>{paramVal.name}</option>
              ))}
            </SelectField>
          ) : (
            <TextField
              name="parameterValue"
              label={<LabelRequired text={strings.fields.value.label} />}
              value={equipmentTypeParameterForm.parameterValue}
              onChange={onChange}
              type={parameterSelected?.type ?? "text"}
              errorMessage={errors.parameterValue}
              hasError={!!errors.parameterValue}
            ></TextField>
          )}
        </Grid>
        <Flex justifyContent="end">
          <Button
            children={strings.fields.buttons.reset}
            onClick={resetFormValues}
          ></Button>
          <Button
            children={
              type === "create"
                ? strings.fields.buttons.create
                : strings.fields.buttons.save
            }
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          ></Button>
        </Flex>
      </Grid>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      name: {
        label: "Nombre",
      },
      value: {
        label: "Valor",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    validations: {
      incorrectValue: "Valor incorrecto",
    },
    responses: {
      errorFetchParameters: "Error al consultar parámetros",
    },
  },
});

export default EquipmentTypeParameterForm;
